import { useEffect, useRef, useState } from 'react';

import { faWaveformLines } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faExclamationTriangle,
  faMicrophone,
  faMicrophoneSlash,
  faPlay,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src';
import {
  formatTime,
  getAudioRecorder,
  isActiveAnnotatorRouteAtom,
  isNoteGenerationActiveAtom,
  isRecorderSheetOpenAtom,
  isRecordingSessionActiveAtom,
  isRecordingSessionPausedAtom,
  isSilentAtom,
  recordingDurationAtom,
  silenceSeverityAtom,
  uiStatusAtom,
  useRecordingTimer,
} from '@repo/videa-notes';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';

import {
  selectedUnifiedIdAtom,
  selectedVisitAtom,
} from '@/app/appHeader/imageViewerMenu/atoms';
import type { VideaNotesAppointment } from '@/modules/videaNotes/helpers';
import {
  VideaNotesUIState,
  useOpenVideaNotes,
} from '@/modules/videaNotes/helpers';
import { useExistingNotes } from '@/react-query/videa-notes/useExistingNotes';

import { getAnnotatorPatientIdFromHash } from '../../../app/appHeader/path-utils';
import { appointmentPatientsAtom } from '../../patientList/atoms';
import { VoiceNotesSource } from '../types';

export const VideaNotesButton = () => {
  // Redux selectors
  const user = useSelector((state: any) => state?.login?.user);
  const patients = useSelector((state: any) => state.annotator?.patients) || {};

  // atoms
  const isRecordingSessionActive = useAtomValue(isRecordingSessionActiveAtom);
  const isRecordingSessionPaused = useAtomValue(isRecordingSessionPausedAtom);
  const isNoteGenerationActive = useAtomValue(isNoteGenerationActiveAtom);
  const isActiveAnnotatorRoute = useAtomValue(isActiveAnnotatorRouteAtom);
  const isMuted = useAtomValue(isSilentAtom);
  const silenceSeverity = useAtomValue(silenceSeverityAtom);
  const duration = useAtomValue(recordingDurationAtom);
  const isSheetOpen = useAtomValue(isRecorderSheetOpenAtom);
  const appointmentPatients = useAtomValue(appointmentPatientsAtom);
  const estimatedVisitIdAtomValue: string | null =
    useAtomValue(selectedVisitAtom);
  const unifiedAppointmentId = useAtomValue(selectedUnifiedIdAtom);

  const setIsVideaNotesSheetOpen = useSetAtom(isRecorderSheetOpenAtom);
  const setUiStatus = useSetAtom(uiStatusAtom);

  // Once dismissed, the popover stays hidden for the entire recording session.
  // Reset when a new recording session starts.
  const [isDismissed, setIsDismissed] = useState(false);
  const prevRecordingActiveRef = useRef(isRecordingSessionActive);
  useEffect(() => {
    if (prevRecordingActiveRef.current && !isRecordingSessionActive) {
      setIsDismissed(false);
    }
    prevRecordingActiveRef.current = isRecordingSessionActive;
  }, [isRecordingSessionActive]);

  // hooks
  const openVideaNotes = useOpenVideaNotes();
  const imagingPatientId = getAnnotatorPatientIdFromHash() ?? undefined;

  useRecordingTimer(); // hook for the recording duration timer

  let matchingPatient = appointmentPatients.filter(
    (p) => p.imagingPatientId === imagingPatientId,
  );

  const secondaryMatchingPatient =
    (imagingPatientId && patients[imagingPatientId]) || {};

  const estimatedVisitId = estimatedVisitIdAtomValue
    ? parseInt(estimatedVisitIdAtomValue)
    : null;

  // Check for existing notes for this appointment
  const { data: existingNotes = [] } = useExistingNotes(
    unifiedAppointmentId || undefined,
    isActiveAnnotatorRoute &&
      !isRecordingSessionActive &&
      !!unifiedAppointmentId,
  );

  // Check if button should be disabled
  const isButtonDisabled = isActiveAnnotatorRoute && !unifiedAppointmentId;

  // handlers
  const handleOpenVideaNotes = () => {
    if (isActiveAnnotatorRoute && !isRecordingSessionActive) {
      // Early return if no unified appointment ID
      if (!unifiedAppointmentId) {
        return;
      }

      // Early return if no matching patient
      if (!matchingPatient || matchingPatient.length === 0) {
        if (secondaryMatchingPatient.name) {
          matchingPatient = [
            {
              firstName: secondaryMatchingPatient.name,
              videaId: imagingPatientId,
            },
          ];
        } else {
          return;
        }
      }

      const patient = matchingPatient[0];

      // Create a complete appointment object matching the Appointment interface
      const appointmentData: VideaNotesAppointment = {
        patient,
        appointment: {
          unifiedId: unifiedAppointmentId,
          start: new Date().toISOString(), // Required field, using current date as fallback
          estimatedVisitId: estimatedVisitId,
        },
        viewed: false, // Required field
        lastTreatments: {
          lastBw: null,
          lastFluoride: null,
          lastFmx: null,
          lastPerioChart: null,
          lastPerioMaintenance: null,
          lastProphy: null,
          lastSrp: null,
        },
      };

      // If there are existing notes, open to SUMMARY_RESULT view
      // Otherwise, open to AUDIO_CAPTURE view
      const uiState =
        existingNotes.length > 0
          ? VideaNotesUIState.SUMMARY_RESULT
          : VideaNotesUIState.AUDIO_CAPTURE;
      openVideaNotes(
        user,
        appointmentData,
        uiState,
        VoiceNotesSource.VOICE_NOTES_BUTTON,
      );
    } else {
      // When recording is active, ensure we show the recording view (AUDIO_CAPTURE)
      if (isRecordingSessionActive) {
        setUiStatus(VideaNotesUIState.AUDIO_CAPTURE);
      }
      setIsVideaNotesSheetOpen(true);
    }
  };

  const handleResumeRecordingSession = () => {
    getAudioRecorder().resume?.();
  };

  const handleDismissPopover = () => {
    setIsDismissed(true);
  };

  // Popover auto-shows when muted, sheet is closed, and user hasn't dismissed it
  const isPopoverVisible =
    isMuted &&
    isRecordingSessionActive &&
    !isRecordingSessionPaused &&
    !isSheetOpen &&
    !isDismissed;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <>
          {/* a recording has been started but is currently paused */}
          {isRecordingSessionPaused && (
            <Button
              className="lg:vai-w-[208px]"
              variant="destructive"
              onClick={handleResumeRecordingSession}
            >
              <FontAwesomeIcon
                icon={faPlay}
                className="vai-h-4 vai-w-4 vai-text-white"
              />{' '}
              Resume
            </Button>
          )}

          {/* a recording is in an active state - show "No Audio" notice when no audio detected */}
          {isRecordingSessionActive && !isRecordingSessionPaused && (
            <Popover open={isPopoverVisible}>
              <PopoverTrigger asChild>
                <Button
                  className={`lg:vai-w-[208px] ${
                    isMuted
                      ? '!vai-bg-[#f97907] hover:!vai-bg-[#e06e06] !vai-text-[#fef2f2]'
                      : ''
                  }`}
                  variant={isMuted ? 'default' : 'destructivePulse'}
                  onClick={handleOpenVideaNotes}
                >
                  <FontAwesomeIcon
                    icon={isMuted ? faMicrophoneSlash : faWaveformLines}
                    className={`vai-h-4 vai-w-4 ${isMuted ? 'vai-text-[#fef2f2]' : 'vai-text-white'}`}
                  />
                  {isMuted ? ' No Audio' : ` ${formatTime(duration)}`}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="center"
                sideOffset={8}
                className="vai-w-80 vai-p-4 vai-z-[300] !vai-bg-[#fff8eb] !vai-border-[#ffd588] vai-shadow-lg"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <div className="vai-flex vai-flex-col vai-items-center vai-gap-1 vai-text-center">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="vai-text-[#ff9c1b] vai-h-4 vai-w-4 vai-mb-1"
                  />
                  <p className="vai-font-medium vai-text-sm vai-text-[#18181b]">
                    {silenceSeverity === 'late'
                      ? "You\u2019ve been quiet for a bit"
                      : "We\u2019re not picking up any audio"}
                  </p>
                  <p className="vai-text-sm vai-text-[#4b5563] vai-opacity-90">
                    {silenceSeverity === 'late'
                      ? "Your recording is still running. End it when you\u2019re ready, or keep speaking if you\u2019re not done."
                      : 'Make sure your mic is unmuted and connected \u2013 then you\u2019re all set.'}
                  </p>
                  <div className="vai-flex vai-items-center vai-justify-center vai-pt-4 vai-w-full">
                    <Button
                      variant="outline"
                      onClick={handleDismissPopover}
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* generation of a clinical note has been requested, stopping the recording session */}
          {isNoteGenerationActive && (
            <Button
              className="vai-w-[208px]"
              variant="destructive"
              onClick={handleOpenVideaNotes}
            >
              <FontAwesomeIcon
                icon={faSpinner}
                className="vai-h-4 vai-w-4 vai-text-white vai-animate-spin"
              />
              Processing
            </Button>
          )}

          {/* base case, where no active recording session exists */}
          {!isRecordingSessionActive && !isNoteGenerationActive && (
            <Button
              variant="outline"
              className="vai-bg-brand-primary hover:vai-bg-brand-primary/90 vai-text-black hover:vai-text-black vai-border-none"
              onClick={handleOpenVideaNotes}
              disabled={isButtonDisabled}
            >
              <FontAwesomeIcon
                icon={faMicrophone}
                className="vai-h-4 vai-w-4 vai-text-black"
              />
              {' Start Note'}
            </Button>
          )}
        </>
      </TooltipTrigger>
      <TooltipContent>
        <span>
          <Trans>Videa Notes</Trans>
        </span>
      </TooltipContent>
    </Tooltip>
  );
};
